import * as yup from 'yup' 
import deepFreeze from 'deep-freeze'
import assign from 'lodash/assign'
import merge from 'lodash/merge'
import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'

import { getIn } from 'formik'

/*
 * Resource Module
 */

// TODO: dependant fields check - throw error if fields are invoked without their dependant


/*
 * Field types:
 * - object (nested props)
 * - text (single line text)
 * - textarea (block text)
 * - date
 * - enum
 * - yesNo
 * - email
 * - file
 * - fileArray
 */

yup.setLocale({
  mixed: {
  	// TODO: give better error
    required: 'This field is required',
  },
  string: {
  	// eslint-disable-next-line
    min: "Must be at least ${min} characters long",
    // eslint-disable-next-line
    max: "Must be less than ${max} characters",
    email: 'Invalid email address',
  },
});


// Value formatting helpers 
export const format_booleanToYesNo = val => typeof val === 'boolean' ? val ? 'Yes' : 'No' : '';
export const format_useLabel = val => val && val.label;

export const makeValidator = (base, strictHandler) => {
	return ({strictMode}) => strictMode ? strictHandler(base) : base;
}

// Yup transform
function emptyStringToNull(value, originalValue) {
  if (typeof originalValue === 'string' && originalValue === '') {
    return null;
  }
  return value;
}

export const fieldTypes = deepFreeze({
	time: {
		defaultValue: '',
		// TODO: implement time field!
	},
	number: {
		defaultValue: '',
		validator: yup.number().transform(emptyStringToNull).nullable(),
		// validator: yup.number(),
	},

	phone: {
		defaultValue: '',
		validator: yup.string().transform(emptyStringToNull).nullable().min(10),
			fieldProps: {
				component: 'PhoneInput'
			}
	},

	object: {},
	password: {
		defaultValue: '',
		validator: yup.string().min(8),
	},
	text: {
		defaultValue: '',
		validator: yup.string(),
	},
	textarea: {
		defaultValue: '',
		validator: yup.string(),
		fieldProps: {
			component: 'textarea',
		}
	},
	checkbox: {
		defaultValue: false,
		validator: yup.boolean(),
		fieldProps: {
			component: 'Checkbox'
		},
		toCSV: {
			formatValue: format_booleanToYesNo,
		}
	},
	date: {
		defaultValue: null,
		fieldProps: {
			component: 'DatePicker',
		},
		validator: yup.string().nullable(),
		toCSV: {
			formatValue: val => val && moment(val).format('DD/MM/YYYY'),
		}
	},
	select: {
		defaultValue: '',
		fieldProps: {
			component: 'Select',
			options: [],
		},
		validator: yup.mixed(),
	},
	radioGroup: {
		defaultValue: '',
		fieldProps: {
			component: 'RadioGroup',
		},
		validator: yup.string(),
	},
	yesNo: {
		defaultValue: undefined,
		fieldProps: {
			component: 'RadioGroup',
			options: [
				{value: true, label: 'Yes'},
				{value: false, label: 'No'}
			],
		},
		validator: yup.boolean(),
		toCSV: {
			formatValue: format_booleanToYesNo,
		}
	},
	resourceSelect: {
		defaultValue: null,
		fieldProps: {
			component: 'ResourceSelect',
		},
		validator: yup.mixed(), // TODO: validate resource select
		toCSV: {
			formatValue: format_useLabel,
		}
	},
	email: {
		defaultValue: '',
		fieldProps: {
			type: 'text',
		},
		validator: yup.string().email(),
	},
	// array: {},
	fieldArray: {
		defaultLength: 0,
		fieldProps: {
			component: 'FieldArray',
		},
		toCSV: {
			getPreRenderArgs: (data, key) => {
				if(Array.isArray(data)) {
					var longest = 1;
					for(let item of data) {
						if(item[key] && item[key].length > longest) {
							longest = item[key].length;
						}
					}
					return { longest };
				} else {
					return {longest: data[key].length};
				}
			}
		}
	},
	file: {
		defaultValue: '',
		required: 'never',
		validator: yup.mixed(), // TODO: validate file
		fieldProps: {
			component: "FileUpload",
		},
		toCSV: {
			include: 'never',
		},
	},
	fileArray: {
		defaultValue: [],
		required: 'never',
		validator: yup.mixed(), // TODO: validate file array
		fieldProps: {
			component: "FileUploadMulti",
		},
		toCSV: {
			include: 'never',
		},
	},
});

const OptionSchema = yup.object({
	label: yup.string().required(),
	value: yup.mixed().required(),
})

const CSVRenderSchema = yup.object({
	pre: yup.mixed().isType('function'),
	header: yup.mixed(), // TODO: allow function or primitive value(s) 
	cell: yup.mixed().isType('function'),  
});

const ResourceFieldSchema = yup.object({
	key: yup.string().required('ResourceFieldSchema.'),
	type: yup.string().oneOf(Object.keys(fieldTypes)).required(),
	label: yup.string(),
	validator: yup.mixed(),
	required: yup.string().oneOf(['always', 'never', 'conditional', 'auto']),
	fieldProps: yup.object({
		options: yup.array().of(OptionSchema), // TODO: make required when using select type(s)
		component: yup.string(),
	}),
	toCSV: yup.object({
		include: yup.string().oneOf(['always', 'never', 'auto']),
		formatValue: yup.mixed(),
		// singular: CSVRenderSchema,
		// list: CSVRenderSchema
	}).default({ 
		include: 'auto',
	}),
	// Type specific schema props
	children: yup.array().of(yup.lazy(() => ResourceFieldSchema)), // TODO: throw error if not array/object
});

const ResourceModelSchema = yup.array().of(ResourceFieldSchema);
const ResourceModelOptionsSchema = yup.object().shape({
	toCSV: yup.mixed(), // TODO: validate toCSV options
});

const isFieldWithOverrides = field => {
	return (Array.isArray(field)
		&& field.length === 2 
		&& typeof field[0] === 'string' 
		&& typeof field[1] === 'object' 
		&& !Array.isArray(field[1])
	) 
};

class ResourceField {
	constructor(path, { fieldProps, ...props}) {
		this.path = path;
		
		for(let key in props) {
			this[key] = props[key];
		}

		this.fieldProps = this.makeFieldProps(fieldProps);

	}

	getFieldProps() {
		return this.type === 'object' && !this.fieldProps.component
			? this.getChildFieldProps()
			: this.fieldProps;
	}

	getChildFieldProps() {
		const fieldProps = []
		for(const field of this.children) {
			fieldProps.push(field.getFieldProps());
		}
		return fieldProps;
	}

	getChildPaths() {
		var paths = [];
		for(const field of this.children) {
			if(field.type === 'object' || field.type === 'fieldArray') {
				const subPaths = field.getChildPaths();
				paths = [...paths, ...subPaths];
			}
			else {
				paths.push(field.path); 
			}
		}
		return paths;
	}

	getChildKeys() {
		var keys = [];
		for(const field of this.children) {
			if(field.type === 'object' || field.type === 'fieldArray') {
				const subKeys = field.getChildKeys();
				keys = [...keys, ...subKeys];
			}
			else {
				keys.push(field.key); 
			}
		}
		return keys;
	}

	getChildDefaults() {
		const defaults = {};
		for(let field of this.children) {
			defaults[field.key] = field.getDefault();
		}
		return defaults;
	}

	makeFieldProps(fieldProps) {
		const props = {
			...fieldProps,
			name: this.path,
			label: this.label,
		};
		if(['text','number', 'password', 'email'].includes(this.type)) {
			props.type = this.type === 'email' ? 'text' : this.type;
		}
		return props;
	}

	getDefault() {

		if('defaultValue' in this) {
			return cloneDeep(this.defaultValue);
		} 
		else if(this.type === 'object') {
			return this.getChildDefaults();
		}
		else if(this.type === 'fieldArray' && !isNaN(this.defaultLength)) {
			const defaultValue = []; 
			for(let i = 0; i < this.defaultLength; i++) {
				defaultValue.push(this.getChildDefaults());
			}
			return defaultValue;
		}

	}

	getChildValidators({ paths, ...options }) {
		const subValidators = {};
		if(this.children && this.children.length) {
			for(let field of this.children) {
				let subValidator = field.getValidator(options);
				if(subValidator && (!Array.isArray(paths) || paths.includes(field.key) )) {
					subValidators[field.key] = subValidator;
				}
			}
		}
		return subValidators;
	}

	getValidator(options) {

		const required = this.required ? this.required : 'auto';
		const {strictMode, includePrivate, paths} = options;

		if(this.private && !includePrivate) 
			return false;

		if((this.type === 'fieldArray' || this.type === 'object')) {
			const childValidators = this.getChildValidators(options);
			const subSchema =  yup.object().shape(childValidators);

			if(typeof this.validator === 'function') {
				// for fieldArray/object validator can either be:
				// (subSchema, options) => return yup schema
				// subSchema => options => return yup schema 
				const result = this.validator(subSchema, options);
				return typeof result === 'function'
					? result(options)
					: result;
			}
			else if(this.type === 'object') {
				return Object.keys(childValidators).length ? subSchema : false;
			}
			else if(this.type === 'fieldArray') {
				return Object.keys(childValidators).length ? yup.array().of(subSchema): yup.array();
			}
		}

		if(!this.validator) 
			return false;

		// if validator is a function apply with options
		const validator = typeof this.validator === 'function'
			? this.validator(options)
			: this.validator;

		if(required === 'always' || (strictMode && required === 'auto')) {
			return validator.required();
		}
		else {
			return validator;
		}

	}

	getCSVHeaders(preRenderArgs, include, prefix="") {

		var headers = [];

		if(this.type === 'fieldArray') {
			for(let i = 0; i < preRenderArgs.longest; i++) {
				for(let childField of this.children) {

					if(include == null || include[childField.key]) {
						const childHeaders = childField.getCSVHeaders(preRenderArgs, undefined, `${this.label} [${i+1}] - `);
						headers = [...headers, ...childHeaders];
					}
				}
			}
		}
		else if(this.type === 'object') {
			for(let childField of this.children) {
				
				if(!include || include[childField.key]) {
					const childHeaders = childField.getCSVHeaders(preRenderArgs, undefined, prefix);
					headers = [...headers, ...childHeaders];
				}
			}
		}
		else if(this.toCSV.valueIsArray) {
			for(let i = 0; i < preRenderArgs.longest; i++) {
				headers.push(prefix+this.label+` [${i+1}]`);
			}
		}
		else {
			headers.push(prefix+this.label);
		}

		return headers;
	}

	getCSVValues(preRenderArgs, include, data={}, basePath) {
		var values = [];
		const { type, toCSV: {formatValue, valueIsArray} } = this;

		if(type === 'fieldArray') {
			for(let i = 0; i < preRenderArgs.longest; i++) {
				const row = data[this.key][i] || {};
				for(let childField of this.children) {

					if(null == include || include[childField.key]) {

						const childValues = childField.getCSVValues(preRenderArgs, undefined, row);
						values = [...values, ...childValues];
					}
				}
			}
		}
		else if(type === 'object') {
			for(let childField of this.children) {
				
				if(null == include || include[childField.key]) {
					const childValues = childField.getCSVValues(preRenderArgs, undefined, data[this.key]);
					values = [...values, ...childValues];
				}
			}
		}
		else if(valueIsArray) {
			let valueArray = data[this.key];
			values = Array.apply(null, Array(preRenderArgs.longest)).map((v, idx) => {
				return idx in valueArray ? formatValue(valueArray[idx]) : '';
			});
		}
		else {
			let value = data[this.key];
			value = typeof formatValue === 'function' ? formatValue(value) : value;
			values.push(String(value || ''));
		}

		return values;
	}

}

export class ResourceModel {

	constructor(options, schema) {
		// apply defaults
		this.applyDefaults(schema);
		// validate schema
		schema = this.validate(ResourceModelSchema, schema);
		// convert schema to ResourceField objects
		this.castSchema(schema);
		// store immutable config
		this.schema = deepFreeze(schema);

		// validate options
		options = this.validate(ResourceModelOptionsSchema, options);
		
		const { resourceKey, ..._options } = options;
		
		this.resourceKey = resourceKey;
		this.options = deepFreeze(_options);

		return this;
	}

	validate(ValidationSchema, data) {
		try {
			data = ValidationSchema.validateSync(data);
		}
		catch(err) {
			console.error(err);
			if(err.type === 'required') {
				// TODO: make validation errors easier to find
				throw new Error(`ValidationError: ${err.path} is ${err.type}`);
			}
			else {
				throw new Error(err.message);
			}
		}
		return data;
	}

	applyDefaults(schema) {

		for(let [key, field] of schema.entries()) {

			if(Array.isArray(field.children)) {
				this.applyDefaults(field.children);
			}

			if(fieldTypes[field.type]) {
				schema[key] = merge({}, fieldTypes[field.type], field);
			}

		}

		return schema;

	}

	castSchema(schema, path="") {
		// Convert schema into ResourceField object
		for(let [key, field] of schema.entries()) {
			let fullPath = path+(path.length ? '.' : '')+field.key;
			if(field.children) {
				field.children = this.castSchema(field.children, fullPath);
			}

			schema[key] = new ResourceField(fullPath, field);
		}
		return schema;
	}

	extend(options, schema) {

		// 1. create combined schema by merging passed schema with this.schema
		const schemaKeyMap = this.schemaToKeyMap(schema)

		// append all root level fields not found in extending schema
		for(let i = this.schema.length - 1; i >= 0; i--) {
			const field = this.schema[i];
			if(!schemaKeyMap[field.key]) {
				schema.unshift(cloneDeep(field));
			}
		}

		// merge together fields found on both
		for(let path in schemaKeyMap) {
			if(this.keyMap[path]) {
				const original = cloneDeep(this.keyMap[path]);
				const field = schemaKeyMap[path];
				assign(field, assign({}, original, field));
			}
		}	

		// 2. construct new ResourceModel instance and return
		return new ResourceModel(options, schema);
	}

	// 
	// Getters
	// 

	field(path) {
		// get field instance for path or array of paths

		if(Array.isArray(path)) {
			return path.map(::this.field);
		}

		if(!this.keyMap[path]) 
			throw new Error(`Invalid field ${path}`);

		if(this._buffering && !this._buffer.includes(path)) {
			this._buffer.push(path);
		}

		return this.keyMap[path];

	}

	getDefaults(schema, defaults={}) {
		// constructs defaults object from schema / path

		// input can be:
		// - string path of basic path -- return basic value
		// - string path of 
		// - array of string path

		if(typeof schema === 'string') {
			return this.field(schema).getDefault();
		}

		if(isFieldWithOverrides(schema)) {
			return this.field(schema[0]).getDefault();
		}

		if(schema instanceof ResourceField) {
			return schema.getDefault();
		}

		if(schema == null) {
			schema = this.schema;
		}

		for(let field of schema) {
			if(typeof field === 'string') {
				field = this.field(field);
			}

			if(isFieldWithOverrides(field)) {
				field = this.field(field[0]);
			}

			defaults[field.key] = field.getDefault();
		}

		return defaults;
	}

	getFieldProps(path) {

		var overrides;

		path = path ? path : this.rootKeys;

		if(Array.isArray(path)) {
			if(isFieldWithOverrides(path)) {
				overrides = path[1];
				path = path[0];
			}
			else {
				var result = [];

				for(const p of path) {
					const fieldProps = this.getFieldProps(p);
					if(Array.isArray(fieldProps)) {
						// fieldProps has returned array of fieldProps
						result = [...result, ...fieldProps];
					}
					else {
						result.push(fieldProps);
					}
				}
				
				return result;
			}
		}

		// TODO: add support for fieldProps overrides here

		return overrides
			? merge({}, this.field(path).fieldProps, overrides)
			: this.field(path).getFieldProps();
	}

	getFieldChecklistNodes(schema) {
		schema = schema ? schema : this.schema;

		const nodes = [];

		for(const field of schema) {

			if(field.toCSV.include === 'never') continue;

			const node = {
				value: field.path,
				label: field.label,
			};

			if(field.toCSV.include === 'always') {
				node.disabled = true;
			}

			if(field.children && field.children.length) {
				node.children = this.getFieldChecklistNodes(field.children);
			}

			nodes.push(node);

		}

		return nodes;
	}

	getValidationSchema(paths, props) {
		
		if(props === undefined && (typeof paths === 'object' && !Array.isArray(paths))) {
			props = paths;
			paths = null;
		}

		paths = typeof paths === 'string' || isFieldWithOverrides(paths) ? [paths] : paths;
		paths = paths ? paths : this.rootKeys;

		props = props || { strictMode: true, includePrivate: false, isNew: false };

		if(props.isNew != null && props.includePrivate == null) {
			props.includePrivate = props.isNew;
		}

		const baseSchema = yup.object();
		const schemaFields = {};
		const dotPathFields = {};

		for(let path of paths) {

			if(isFieldWithOverrides(path)) {
				path = path[0];
			}

			if(path.indexOf('.') === -1) {
				const field = this.field(path);
				const validator = field.getValidator(props);
				if(!validator) continue;
				schemaFields[field.key] = validator;
			} else {
				const splitPath = path.split('.');
				const [ basePath, key ] = splitPath;

				if(splitPath.length > 2) {
					throw new Error('deep dotpaths not currently supported for validationSchemas');
				}

				if(dotPathFields[basePath]) {
					dotPathFields[basePath].push(key);
				} else {
					dotPathFields[basePath] = [key];
				}

			}

		}

		// add dot-path filtered object/fieldArray fields
		if(Object.keys(dotPathFields).length) {
			for(let basePath in dotPathFields) {
				const field = this.field(basePath);
				const validator = field.getValidator({ ...props, paths: dotPathFields[basePath] })
				if(!validator) continue;
				if(schemaFields[basePath]) {
					console.log(`warning: validator for ${basePath} overwritten by dot paths`);
				}
				schemaFields[basePath] = validator;
			}
		}

		return baseSchema.shape(schemaFields);
	}

	//
	// Field Maps
	//

	schemaToKeyMap(array, lookup={}, path="") {
		this.recurseFields(array, (field, fullPath) => {
			lookup[fullPath] = field;
		});
		return lookup;
	}

	schemaToLeafPaths(schema, filter) {
		var keys = [];
		for(let field of schema) {

			if(field.children) {
				const childPaths = this.schemaToLeafPaths(field.children);
				keys = [...keys, ...childPaths];
			} 
			else if(typeof filter === 'function' && filter(field) || !filter) {
				keys.push(field.path);
			}

		}
		return keys;
	}

	getLeafPaths(filter) {
		return this.schemaToLeafPaths(this.schema, filter);
	}

	get keyMap() {
		this._keyMap = this._keyMap || this.schemaToKeyMap(this.schema);
		return this._keyMap;
	}

	get rootKeys() {
		return Object.keys(this.keyMap).filter(key => key.indexOf('.') === -1);
	}

	get leafPaths() {
		this._leafKeys = this._leafKeys || this.schemaToLeafPaths(this.schema);
		return this._leafKeys
	}



	//
	// CSV Export
	//

	mapDataToCSV(data, paths) {
		const isObject = typeof data === 'object';
		const isArray = Array.isArray(data);

		if(isArray) {
			return this.mapDataToCSVPlural(data, paths);
		}
		else if(isObject) {
			return this.mapDataToCSVSingular(data, paths);
		}
		else {
			throw new Error('invalid data')
		}
		
	}

	makeCSVKeyMap(includePaths, schema) {

		var paths = {};

		// iterate over schema paths in order checking if paths contains the fields
		for(let field of schema) {
			if(field.type === 'object' || field.type === 'fieldArray') {

				if(field.toCSV.include !== 'never') {

					var subPaths = false;
					// check for root path in includePaths
					if(includePaths == null || includePaths.includes(field.path)) {
						// root path exists, include all childPaths
						subPaths = this.makeCSVKeyMap(null, field.children);

					} else {
						subPaths = this.makeCSVKeyMap(includePaths, field.children);
					}

					if(Object.keys(subPaths).length) {
						paths[field.key] = subPaths;
					}

				}

			}
			else if(
				(includePaths == null || includePaths.includes(field.path)) && field.toCSV.include !== 'never'
				|| field.toCSV.include === 'always') {
				paths[field.key] = true;
			}
		}

		return paths;

	}

	mapDataToCSVSingular(data, paths) {
		throw new Error('mapDataToCSVSingualr not yet implemented');
		//TODO: implement mapDataToCSVSingular
	}

	iterateCSVKeyMap(paths, schema, callback) {
		for(const field of schema) {
			if(paths[field.key]) {
				callback(field.key, field, paths[field.key]);
			}
		}

	}

	mapDataToCSVPlural(data, includePaths) {

		const keyMap = this.makeCSVKeyMap(includePaths, this.schema);

		//
		// Get Pre-Render Arguments
		//

		const preRenderArgs = {};
		this.iterateCSVKeyMap(keyMap, this.schema, (key, field, include) => {
			if(typeof field.toCSV.getPreRenderArgs === 'function') {
				preRenderArgs[key] = field.toCSV.getPreRenderArgs(data, key, include);
			}
		});

		//
		// Get CSV Headers
		//
		var headers = [];
		this.iterateCSVKeyMap(keyMap, this.schema, (key, field, include) => {
			const fieldHeaders = field.getCSVHeaders(preRenderArgs[key], include);
			headers = [...headers, ...fieldHeaders];
		});

		//
		//  Get CSV Rows
		//

		var rows = [];
		for(const resource of data) {
			const row = [];
			this.iterateCSVKeyMap(keyMap, this.schema, (key, field, include) => {
				const fieldValues = field.getCSVValues(preRenderArgs[key], include, resource);
				row = [...row, ...fieldValues];
			});
			rows.push(row);
		}

		return [headers, ...rows];
	}

	//
	// Helpers
	//

	recurseFields(schema, callback, path="") {
		for(let field of schema) {
			let fullPath = path+(path.length ? '.' : '')+field.key;
			callback(field, fullPath);
			if(field.children) {
				this.recurseFields(field.children, callback, fullPath);
			}
		}
	}

	//
	// Field select buffer
	//

	startFieldBuffer() {
		this._buffering = true;
		this._buffer = [];
	}

	endFieldBuffer() {
		const result = this._buffer;
		this._buffer = [];
		this._buffering = false;
		return result;
	}

}

//
// Helpers
//

// test to validate arguments as ResourceModel instances
// usage: yup.mixed().test(isResourceModelTest) 
export const isResourceModelTest = {
	name: 'resource-model-test',
	message: 'must be an instanceof ResourceModel',
	test: value => {
		const isObject = typeof value === 'object';
		const instanceOf = value instanceof ResourceModel;
		return !value || isObject && instanceOf;
	}
}

// TODO: make it so one doesn't have to explicitly define object fields
// TODO: make is so one can define schema in less nested format
// TODO: make payrollNo an abstracted type??

// TODO: move this function to util somewhere
export function makeOptions(options) {
	if(Array.isArray(options)) {
		// covery [ <key> ] to { label: <key>, value: <key> }
		return options.map(k => ({ label: k, value: k }));
	} else {
		// converts key: value to { label: value, value: key }
		return Object.keys(options).map(k => ({
			label: options[k],
			value: k,
		}));
	}
}



// WEBPACK FOOTER //
// ./src/modules/resource/resource.module.js